import type { HistogramData } from '@/api/chart/chart.service'
import { colorForIdStable } from '@/utils/color/palette'

export const makeTotalHistChartData = (
  histQueryResult: HistogramData,
  schoolColors: { [id: string]: string },
  seriesLabels?: { [id: string]: string },
) => {
  // ✅ Null/undefined 체크 추가
  if (!histQueryResult) {
    console.warn('[makeTotalHistChartData] histQueryResult is null or undefined')
    return { xTicks: [], chartData: [] }
  }

  const labels = histQueryResult.labels
  // ✅ labels가 undefined일 수 있으므로 체크 추가
  if (!labels || !Array.isArray(labels)) {
    console.warn('[makeTotalHistChartData] labels is not an array:', labels)
    return { xTicks: [], chartData: [] }
  }

  if (labels.length === 0) {
    return { xTicks: [], chartData: [] }
  }

  // ✅ histogramPerSchoolId도 체크
  if (!histQueryResult.histogramPerSchoolId) {
    console.warn('[makeTotalHistChartData] histogramPerSchoolId is missing')
    return { xTicks: labels, chartData: [] }
  }
  if (labels.length === 1) {
    const newLabel = [
      parseFloat(labels[0].toFixed(2)) - 1,
      parseFloat(labels[0].toFixed(2)),
      parseFloat(labels[0].toFixed(2)) + 1,
    ]
    // ✅ 해당 날짜에 데이터가 있는 school만 필터링
    const chartData = Object.entries(histQueryResult.histogramPerSchoolId)
      .filter((entry) => {
        const values = entry[1]
        if (!Array.isArray(values)) return false
        const sum = values.reduce((a, b) => a + b, 0)
        return sum > 0 // 데이터가 있는 school만 포함
      })
      .map((entry) => {
        const schoolId = entry[0]
        return {
          id: seriesLabels?.[schoolId] ?? schoolId,
          data: [
            { x: newLabel[0], y: '0' },
            { x: newLabel[1], y: '100.0' },
            { x: newLabel[2], y: '0' },
          ],
          color:
            schoolColors[schoolId] !== undefined
              ? schoolColors[schoolId]
              : colorForIdStable(schoolId),
        }
      })
    return {
      xTicks: newLabel,
      chartData: chartData,
    }
  }

  // ✅ 해당 날짜에 데이터가 있는 school만 필터링
  const chartData = Object.entries(histQueryResult.histogramPerSchoolId)
    .filter((entry) => {
      const values = entry[1]
      if (!Array.isArray(values)) return false
      const sum = values.reduce((a, b) => a + b, 0)
      return sum > 0 // 데이터가 있는 school만 포함
    })
    .map((entry) => {
      const schoolId = entry[0]
      const values = entry[1]
      
      // ✅ 배열 길이 검증: labels는 구간(bin) 경계값들, bins는 구간 개수
      // 예: labels = [0, 10, 20, 30] → bins = [0-10), [10-20), [20-30) → 3개
      // 따라서 labels가 n개면 bins(구간)는 n-1개여야 함
      const expectedLength = labels.length - 1
      if (values.length !== expectedLength) {
        console.warn(
          `[makeTotalHistChartData] Array length mismatch for ${schoolId}: ` +
            `values.length=${values.length}, expected=${expectedLength} (labels.length=${labels.length})`
        )
        return null // 필터링을 위해 null 반환
      }
      
      // ✅ 히스토그램 값들을 정규화 (합계로 나누어 비율로 변환)
      const sum = values.reduce((a, b) => a + b, 0)
      const normalizedValues = sum > 0 ? values.map((v) => v / sum) : values.map(() => 0)
      
      // ✅ 안전한 인덱스 접근으로 차트 데이터 생성
      const data = normalizedValues.map((v, i) => {
        // 인덱스 범위 체크
        if (i + 1 >= labels.length) {
          console.warn(
            `[makeTotalHistChartData] Index out of bounds for ${schoolId}: ` +
              `i=${i}, labels.length=${labels.length}`
          )
          return { x: labels[i] || 0, y: '0' }
        }
        return {
          x: (labels[i] + labels[i + 1]) / 2,
          y: (v * 100).toFixed(2),
        }
      })
      
      return {
        id: seriesLabels?.[schoolId] ?? schoolId,
        data: data,
        color:
          schoolColors[schoolId] !== undefined ? schoolColors[schoolId] : colorForIdStable(schoolId),
      }
    })
    .filter((item) => item !== null) // null 제거 (배열 길이 불일치 케이스)
  return {
    xTicks: labels,
    chartData: chartData,
  }
}
